import React from "react";
import {
  GaugeThreshold,
  defaultColors,
  getValueColorFromThresholds,
  formatGaugeValue,
  valueToLinearPosition,
  BAR_VIEW_WIDTH,
  BAR_PADDING_X,
  BAR_RIGHT_VALUE_WIDTH,
  BAR_FULL_WIDTH,
  BAR_Y_DEFAULT,
  SR_ONLY_STYLE,
  getContrastTextColor,
} from "./gaugeUtils";

export interface StackedSegment {
  value: number;
  color: string;
  label: string;
}

export interface BarRendererProps {
  // Data
  value: number;
  displayValue: number;
  min: number;
  max: number;
  hasValue: boolean;

  // Colors (resolved — no CSS variables)
  resolvedColors: string[];
  resolvedThresholds: GaugeThreshold[];
  valueColor: string;

  // Bar-specific geometry
  barHeight: number;
  barCornerRadius: number;

  // Stacked segments (triggers stacked mode when non-empty)
  stackedSegments?: StackedSegment[];
  showSegmentLabels: boolean;
  showThresholdMarkers: boolean;

  // Display
  label: string;
  unit: string;
  valueFormat: string;
  showValue: boolean;
  showLabel: boolean;
  valuePosition: 'right' | 'above' | 'below';
  valueFontSize: number;
  labelFontSize: number;

  // Styling
  segmentOpacity: number;

  // Identity (for unique clipPath IDs)
  componentId?: string;

  // Accessibility
  ariaLabel?: string;
}

/**
 * Pure horizontal bar gauge renderer.
 * No hooks, no state, no DOM access — receives all data as props.
 */
export const BarRenderer: React.FC<BarRendererProps> = ({
  value,
  displayValue,
  min,
  max,
  hasValue,
  resolvedColors,
  resolvedThresholds,
  valueColor,
  barHeight,
  barCornerRadius,
  stackedSegments,
  showSegmentLabels,
  showThresholdMarkers,
  label,
  unit,
  valueFormat,
  showValue,
  showLabel,
  valuePosition,
  valueFontSize,
  labelFontSize,
  segmentOpacity,
  componentId,
  ariaLabel,
}) => {
  const isStacked = stackedSegments != null && stackedSegments.length > 0;
  const clipId = `bar-clip-${componentId || Math.random().toString(36).slice(2, 9)}`;

  // --- Layout geometry based on valuePosition ---
  const barX = BAR_PADDING_X;
  let viewHeight: number;
  let barY: number;
  let barWidth: number;
  let textX: number;
  let valueTextY: number;
  let textAnchor: 'inherit' | 'start' | 'middle' | 'end';

  if (valuePosition === 'above') {
    barWidth = BAR_FULL_WIDTH;
    const textBlockHeight = valueFontSize + (showLabel && label ? labelFontSize + 2 : 0) + 4;
    barY = textBlockHeight;
    viewHeight = textBlockHeight + barHeight + BAR_Y_DEFAULT;
    textX = BAR_VIEW_WIDTH / 2;
    valueTextY = valueFontSize;
    textAnchor = 'middle';
  } else if (valuePosition === 'below') {
    barWidth = BAR_FULL_WIDTH;
    barY = BAR_Y_DEFAULT;
    const textBlockStart = BAR_Y_DEFAULT + barHeight + 4;
    viewHeight = textBlockStart + valueFontSize + (showLabel && label ? labelFontSize + 4 : 0) + 4;
    textX = BAR_VIEW_WIDTH / 2;
    valueTextY = textBlockStart + valueFontSize;
    textAnchor = 'middle';
  } else {
    // 'right' (default)
    barWidth = BAR_RIGHT_VALUE_WIDTH;
    barY = BAR_Y_DEFAULT;
    viewHeight = BAR_Y_DEFAULT * 2 + barHeight;
    textX = BAR_PADDING_X + barWidth + 10;
    valueTextY = barY + barHeight / 2;
    textAnchor = 'start';
  }

  // --- Display text ---
  let displayText: string;
  if (!hasValue) {
    displayText = '--';
  } else if (Number.isNaN(value) || Number.isNaN(displayValue)) {
    displayText = 'N/A';
  } else {
    displayText = formatGaugeValue(valueFormat, displayValue, max);
  }

  // --- Colors ---
  const trackColor = resolvedColors[1] || defaultColors[1];
  const fillColor = resolvedThresholds.length > 0
    ? getValueColorFromThresholds(value, resolvedThresholds, resolvedColors[0] || defaultColors[0])
    : (resolvedColors[0] || defaultColors[0]);

  // Contrast-safe text color: when threshold colors make valueColor match the fill,
  // swap to a contrast-safe alternative to maintain 4.5:1 ratio (WCAG 2.1 AA)
  const effectiveValueColor = valueColor === fillColor
    ? getContrastTextColor(fillColor)
    : valueColor;

  // --- Stacked segment computation ---
  const segmentRects: Array<{ x: number; width: number; color: string; label: string; value: number }> = [];

  if (isStacked) {
    const sanitized = stackedSegments!.map(s => ({ ...s, value: Math.max(0, s.value) }));
    const totalValue = sanitized.reduce((sum, s) => sum + s.value, 0);

    if (totalValue > 0) {
      const effectiveTotal = Math.max(totalValue, max - min);
      const scale = barWidth / effectiveTotal;
      let currentX = barX;

      for (const seg of sanitized) {
        const w = seg.value * scale;
        segmentRects.push({ x: currentX, width: w, color: seg.color, label: seg.label, value: seg.value });
        currentX += w;
      }
    }
  }

  // --- Fill width for single mode ---
  const fillWidth = hasValue && !Number.isNaN(value)
    ? valueToLinearPosition(value, min, max, barWidth)
    : 0;

  // --- Stacked total for ARIA ---
  const stackedTotal = isStacked
    ? (stackedSegments!.reduce((sum, s) => sum + Math.max(0, s.value), 0))
    : undefined;

  // --- Threshold tick positions (deduplicated) ---
  const thresholdTicks: number[] = [];
  if (!isStacked && showThresholdMarkers && resolvedThresholds.length > 0) {
    const seen = new Set<number>();
    for (const t of resolvedThresholds) {
      if (t.min > min && !seen.has(t.min)) {
        seen.add(t.min);
        thresholdTicks.push(barX + valueToLinearPosition(t.min, min, max, barWidth));
      }
      if (t.max < max && !seen.has(t.max)) {
        seen.add(t.max);
        thresholdTicks.push(barX + valueToLinearPosition(t.max, min, max, barWidth));
      }
    }
  }

  // --- Stacked display text for all-zero case ---
  const stackedDisplayText = isStacked && stackedTotal === 0 ? '0' : displayText;
  const effectiveDisplayText = isStacked ? stackedDisplayText : displayText;

  // --- ARIA ---
  const ariaValueNow = isStacked
    ? (stackedTotal != null && stackedTotal > 0 ? stackedTotal : undefined)
    : (hasValue && !Number.isNaN(value) ? value : undefined);
  const defaultAriaLabel = isStacked
    ? `${label}: ${stackedTotal ?? 0}`
    : (label ? `${label}: ${effectiveDisplayText}` : `Progress: ${effectiveDisplayText}`);

  return (
    <div
      data-testid="gauge-bar-renderer"
      role="progressbar"
      aria-valuenow={ariaValueNow}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={ariaLabel || defaultAriaLabel}
      style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${BAR_VIEW_WIDTH} ${viewHeight}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        data-testid="gauge-bar-svg"
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={barX} y={barY} width={barWidth} height={barHeight}
                  rx={barCornerRadius} ry={barCornerRadius} />
          </clipPath>
        </defs>

        {/* Track background */}
        <rect
          x={barX} y={barY} width={barWidth} height={barHeight}
          rx={barCornerRadius} ry={barCornerRadius}
          fill={trackColor} opacity={0.2}
          data-testid="gauge-bar-track"
        />

        {/* Fill: stacked segments or single bar */}
        <g clipPath={`url(#${clipId})`}>
          {isStacked ? (
            segmentRects.map((seg, i) => (
              <rect
                key={`seg-${i}`}
                x={seg.x} y={barY}
                width={seg.width} height={barHeight}
                fill={seg.color}
                opacity={segmentOpacity}
                style={{ transition: 'width 300ms ease, fill 300ms ease' }}
                data-testid={`gauge-bar-segment-${i}`}
              >
                <title>{`${seg.label}: ${seg.value}`}</title>
              </rect>
            ))
          ) : (
            fillWidth > 0 && (
              <rect
                x={barX} y={barY}
                width={fillWidth} height={barHeight}
                fill={fillColor}
                opacity={segmentOpacity}
                style={{ transition: 'width 300ms ease, fill 300ms ease' }}
                data-testid="gauge-bar-fill"
              >
                <title>{`${label || 'Value'}: ${effectiveDisplayText}${unit ? ' ' + unit : ''}`}</title>
              </rect>
            )
          )}
        </g>

        {/* Threshold tick marks */}
        {thresholdTicks.map((tx, i) => (
          <line
            key={`tick-${i}`}
            x1={tx} y1={barY} x2={tx} y2={barY + barHeight}
            stroke="#9CA3AF" strokeWidth={1} opacity={0.6}
            data-testid={`gauge-bar-tick-${i}`}
          />
        ))}

        {/* Value text */}
        {showValue && (
          <text
            x={textX} y={valueTextY}
            textAnchor={textAnchor}
            dominantBaseline="central"
            fontSize={valueFontSize}
            fontWeight="700"
            fill={effectiveValueColor}
            data-testid="gauge-bar-value"
          >
            {effectiveDisplayText}
            {hasValue && !Number.isNaN(displayValue) && unit && !isStacked && (
              <tspan fontSize={Math.round(valueFontSize * 0.625)}>{unit}</tspan>
            )}
          </text>
        )}

        {/* Label text (right position only — above/below handled via value text area) */}
        {showLabel && label && valuePosition === 'right' && (
          <text
            x={textX}
            y={valueTextY + valueFontSize * 0.65}
            textAnchor={textAnchor}
            dominantBaseline="central"
            fontSize={labelFontSize}
            fontWeight="600"
            fill={effectiveValueColor}
            data-testid="gauge-bar-label"
          >
            {label.length > 12 ? (
              <>
                <title>{label}</title>
                {label.substring(0, 11) + '\u2026'}
              </>
            ) : label}
          </text>
        )}

        {/* Label text for above/below positions */}
        {showLabel && label && valuePosition === 'above' && (
          <text
            x={textX}
            y={valueFontSize + 2 + labelFontSize * 0.85}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={labelFontSize}
            fontWeight="600"
            fill={effectiveValueColor}
            data-testid="gauge-bar-label"
          >
            {label.length > 16 ? (
              <>
                <title>{label}</title>
                {label.substring(0, 15) + '\u2026'}
              </>
            ) : label}
          </text>
        )}

        {showLabel && label && valuePosition === 'below' && (
          <text
            x={textX}
            y={BAR_Y_DEFAULT + barHeight + 4 + valueFontSize + 2 + labelFontSize * 0.85}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={labelFontSize}
            fontWeight="600"
            fill={effectiveValueColor}
            data-testid="gauge-bar-label"
          >
            {label.length > 16 ? (
              <>
                <title>{label}</title>
                {label.substring(0, 15) + '\u2026'}
              </>
            ) : label}
          </text>
        )}
      </svg>

      {/* Segment labels legend (stacked mode only) */}
      {isStacked && showSegmentLabels && stackedSegments!.length >= 1 && (
        <div
          data-testid="gauge-bar-legend"
          style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '4px' }}
        >
          {stackedSegments!.map((seg, i) => (
            <div key={`legend-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
              <div
                style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: seg.color, flexShrink: 0 }}
                data-testid={`gauge-bar-legend-dot-${i}`}
              />
              <span style={{ color: valueColor }}>{seg.label}: {Math.max(0, seg.value)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Screen-reader descriptions for stacked segments */}
      {isStacked && stackedSegments && stackedSegments.length > 0 && (
        <div style={SR_ONLY_STYLE} data-testid="gauge-bar-sr-descriptions">
          {stackedSegments.map((seg, i) => (
            <span key={`sr-seg-${i}`} data-testid={`gauge-bar-sr-item-${i}`}>
              {seg.label}: {Math.max(0, seg.value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
